/**
 * ThinkingIndicator — the "shimmer" shown while Astrid is working a turn.
 *
 * It now reflects TRUE progress: ChatDetail polls the live turn-progress
 * endpoint and passes the actually-running tool via `tool`. When a real tool
 * is running we jump straight into that tool's scenario track; before any tool
 * runs (router still deciding) we lead with a "Understanding your request…"
 * routing phase and best-guess the track from the user's message.
 *
 * Visually it renders like a real assistant bubble (avatar + slate bubble)
 * with an aurora-gradient status line and three shimmering skeleton lines.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { AgentSparkleAvatar } from './v2/AgentSparkleAvatar';
import { toolLabel } from './toolMeta';

const ROUTING_OPENER = 'Understanding your request…';

const TRACK_PHRASES = {
  collector_status: [
    'Checking your collectors…',
    'Reading connection status…',
    'Tallying connected vs verifying…',
    'Spotting anything that needs a retry…',
    'Summarising the results…',
    'Nearly there…',
  ],
  collector_trigger: [
    'Lining up your collectors…',
    'Kicking off collection runs…',
    'Skipping the ones already running…',
    'Confirming what was triggered…',
    'Nearly there…',
  ],
  onboarding: [
    'Reading your credentials file…',
    'Normalising the login details…',
    'Matching each to a supplier…',
    'Preparing a review draft…',
    'Nearly there…',
  ],
  investigation: [
    'Checking recent notes…',
    'Cross-referencing bill records…',
    'Pulling the numbers…',
    'Comparing totals…',
    'Reviewing what came back…',
    'Drafting an explanation…',
    'Nearly there…',
  ],
  payment_file: [
    'Looking into this payment file…',
    'Pulling Funds Pulled lines…',
    'Reviewing Approved Paid bills…',
    'Reconciling the two views…',
    'Double-checking the numbers…',
    'Composing a clear summary…',
    'Nearly there…',
  ],
  report: [
    'Preparing your report…',
    'Pulling the rows we need…',
    'Spinning up the report worker…',
    'Packaging the data…',
    'Almost ready to download…',
  ],
  navigation: [
    'Searching the platform map…',
    'Finding the right page…',
    'Composing a quick path…',
    'Nearly there…',
  ],
  outreach: [
    'Scanning overdue bills…',
    'Reviewing vendor accounts…',
    'Looking up vendor contacts…',
    'Batching account numbers per vendor…',
    'Drafting personalised messages…',
    'Lining them up for your approval…',
    'Nearly there…',
  ],
  document: [
    'Reading your document…',
    'Pulling out the key details…',
    'Summarising the content…',
    'Nearly there…',
  ],
  tasks: [
    'Noting that down…',
    'Saving it to your list…',
    'Nearly there…',
  ],
  data: [
    'Looking into your data…',
    'Pulling a quick read…',
    'Compiling totals…',
    'Reviewing what came back…',
    'Drafting the answer…',
    'Nearly there…',
  ],
  thinking: [
    'Looking into it…',
    'Compiling…',
    'Nearly there…',
  ],
};

// Ordered most-specific → least. First matching row wins (used only until a
// real tool is reported by the backend).
const KEYWORD_TRACKS = [
  { track: 'collector_trigger', kws: [
    'trigger', 're-trigger', 'retrigger', 'run the collector', 'run collectors',
    'run collection', 'collect again', 'run again', 're-run', 'rerun',
    'reconnect', 'kick off', 'kick-off', 'try again',
  ] },
  { track: 'outreach', kws: [
    'email vendor', 'email all vendor', 'email the vendor', 'email overdue',
    'contact vendor', 'contact the vendor', 'reach out to vendor',
    'draft email', 'draft an email', 'draft emails', 'send vendor',
    'request extension', 'request an extension', 'vendor extension',
    'follow up with vendor', 'follow up with vendors', 'vendor outreach',
  ] },
  { track: 'onboarding', kws: [
    'onboard', 'onboarding', 'portal login', 'portal credential',
    'credentials file', 'upload credential', 'csv of login', 'add collectors',
    'add credentials', 'import logins',
  ] },
  { track: 'investigation', kws: ['why', 'mismatch', "doesn't match", 'dont match', "don't match",
    'discrepancy', 'explain', 'investigate', 'off by', 'short by', 'issue', 'wrong', 'differ'] },
  { track: 'payment_file', kws: ['payment file', 'pf ', 'funds pulled', 'fund pulled', 'fund pull',
    'approved paid', 'pulled vs paid', 'pulled paid', 'payment_file', 'pf-'] },
  { track: 'collector_status', kws: [
    'collector', 'collectors', 'any update', 'status of', 'did it work',
    'connected', 'credential status', 'connection status', 'onboarding status',
    'no bills', 'verifying',
  ] },
  { track: 'report', kws: ['report', 'csv', 'export', 'download', 'spreadsheet'] },
  { track: 'navigation', kws: ['where is', 'where can i', 'how do i', 'take me to', 'open the',
    'show me the page', 'navigate'] },
  { track: 'data', kws: ['show', 'list', 'top ', 'how many', 'how much', 'what is', 'whats',
    "what's", 'bills', 'overdue', 'paid', 'unpaid', 'refund', 'variance', 'transaction'] },
];

// Live backend tool → scenario track. Anything unmapped falls back to 'data'.
const TOOL_TRACKS = {
  get_collector_onboarding_status: 'collector_status',
  trigger_collectors: 'collector_trigger',
  onboard_collectors_from_file: 'onboarding',
  confirm_onboard_collectors: 'onboarding',
  generate_csv_report: 'report',
  find_report: 'report',
  describe_page: 'navigation',
  build_deep_link: 'navigation',
  find_overdue_bills_by_vendor: 'outreach',
  draft_email: 'outreach',
  draft_emails_batch: 'outreach',
  approve_emails_batch: 'outreach',
  reconcile_payments: 'payment_file',
  compare_payment_views: 'payment_file',
  analyze_document: 'document',
  create_task: 'tasks',
  create_note: 'tasks',
  query_data: 'data',
  resolve_org: 'data',
  get_user_context: 'data',
  list_drive_files: 'data',
};

const pickTrack = (lastUserMessage = '') => {
  const t = (lastUserMessage || '').toLowerCase();
  for (const row of KEYWORD_TRACKS) {
    if (row.kws.some((k) => t.includes(k))) return row.track;
  }
  return 'data';
};

const ROTATE_MS = 1900;

// Keeps long turns (>~12s) alive so the shimmer never freezes on "Nearly there…".
const SUSTAIN_PHRASES = [
  'Still working on it…',
  'Reviewing the output…',
  'Re-checking the numbers…',
  'Polishing the answer…',
  'Crunching the last bits…',
  'Almost ready…',
  'Wrapping up…',
];

const SKELETON_WIDTHS = ['85%', '70%', '45%'];

// Answer-type-shaped skeleton hint (P0-3). Matches the shimmer to what's coming.
const shapeFor = (trackKey, tool) => {
  if (tool === 'market_snapshot') return 'chart';
  if (trackKey === 'report') return 'doc';
  if (['data', 'investigation', 'payment_file', 'collector_status', 'outreach'].includes(trackKey)) return 'table';
  if (trackKey === 'navigation') return 'lines';
  return 'lines';
};

const SkeletonShape = ({ shape }) => {
  if (shape === 'table') {
    return (
      <div className="mt-2.5 space-y-1.5" aria-hidden="true" data-testid="ai-skel-table">
        <div className="grid grid-cols-4 gap-1.5">
          {[0, 1, 2, 3].map((i) => (
            <div key={`h${i}`} className="ai-skel-line h-2.5" style={{ animationDelay: `${i * 0.12}s` }} />
          ))}
        </div>
        {[0, 1, 2].map((r) => (
          <div key={`r${r}`} className="grid grid-cols-4 gap-1.5">
            {[0, 1, 2, 3].map((c) => (
              <div key={`c${r}${c}`} className="ai-skel-line h-2" style={{ animationDelay: `${(r + c) * 0.08}s`, opacity: 0.7 }} />
            ))}
          </div>
        ))}
      </div>
    );
  }
  if (shape === 'chart') {
    return (
      <div className="mt-2.5 flex items-end gap-1 h-14" aria-hidden="true" data-testid="ai-skel-chart">
        {[40, 65, 50, 80, 60, 90, 72, 100, 55, 78].map((h, i) => (
          <div key={i} className="ai-skel-line flex-1 rounded-t" style={{ height: `${h}%`, animationDelay: `${i * 0.07}s` }} />
        ))}
      </div>
    );
  }
  if (shape === 'doc') {
    return (
      <div className="mt-2.5 space-y-2" aria-hidden="true" data-testid="ai-skel-doc">
        {['90%', '96%', '80%', '60%'].map((w, i) => (
          <div key={w} className="ai-skel-line h-2.5" style={{ width: w, animationDelay: `${i * 0.15}s` }} />
        ))}
        <div className="ai-skel-line h-6 w-28 rounded-lg" style={{ animationDelay: '0.6s' }} />
      </div>
    );
  }
  return (
    <div className="mt-2.5 space-y-2" aria-hidden="true" data-testid="ai-skel-lines">
      {SKELETON_WIDTHS.map((w, i) => (
        <div key={w} className="ai-skel-line h-2.5" style={{ width: w, animationDelay: `${i * 0.18}s` }} />
      ))}
    </div>
  );
};

export const ThinkingIndicator = ({ lastUserMessage = '', tool = null, agentName }) => {
  const resolvedAgentName = agentName
    || (typeof window !== 'undefined' && window.localStorage ? localStorage.getItem('ai_agent_persona_name') : null)
    || 'Astrid';

  const phrases = useMemo(() => {
    if (tool) {
      // A real tool is running — reflect it directly, no routing opener.
      const track = TRACK_PHRASES[TOOL_TRACKS[tool] || 'data'] || TRACK_PHRASES.data;
      return track;
    }
    // Router still deciding — lead with the routing phase, then a best guess.
    const track = TRACK_PHRASES[pickTrack(lastUserMessage)] || TRACK_PHRASES.data;
    return [ROUTING_OPENER, ...track];
  }, [lastUserMessage, tool]);

  const shape = useMemo(() => {
    if (!tool) {
      const msg = (lastUserMessage || '').toLowerCase();
      if (/(market|\bprice|\bprices|\blmp\b|\bspot\b|ercot|pjm|caiso|henry hub|natural gas|power price|energy market|forward curve|\bspp\b)/.test(msg)) {
        return 'chart';
      }
    }
    const trackKey = tool ? (TOOL_TRACKS[tool] || 'data') : pickTrack(lastUserMessage);
    return shapeFor(trackKey, tool);
  }, [lastUserMessage, tool]);

  const [idx, setIdx] = useState(0);

  useEffect(() => { setIdx(0); }, [phrases]);

  useEffect(() => {
    const id = setTimeout(() => setIdx((i) => i + 1), ROTATE_MS);
    return () => clearTimeout(id);
  }, [idx]);

  let phrase;
  if (idx < phrases.length) {
    phrase = phrases[idx];
  } else {
    phrase = SUSTAIN_PHRASES[(idx - phrases.length) % SUSTAIN_PHRASES.length];
  }

  return (
    <div
      data-testid="ai-agent-thinking-indicator"
      className="flex items-start gap-2 max-w-[95%]"
    >
      <div className="mt-0.5">
        <AgentSparkleAvatar name={resolvedAgentName} size={28} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="rounded-2xl rounded-tl-sm bg-slate-50 px-4 py-3">
          <span
            key={phrase /* re-mount so the fade-in re-runs each phrase */}
            data-testid="ai-agent-thinking-phrase"
            className="ai-thinking-text text-sm"
          >
            {phrase}
          </span>
          {tool && (
            <div className="mt-1 text-[11px] text-slate-400" data-testid="ai-thinking-tool-caption">
              {toolLabel(tool)}…
            </div>
          )}
          <div className="mt-2.5" aria-hidden="true">
            <SkeletonShape shape={shape} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThinkingIndicator;
